import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import type { TableColumnEntry } from "./documentEditor";

interface TableColumnsPanelProps {
  /** The table's columns, in the left-to-right order they render. */
  columns: TableColumnEntry[];
  /** Moves a column one step left or right. */
  onMove: (nodeId: string, direction: "left" | "right") => void;
}

/**
 * Properties inspector section reordering a table's columns: one row per
 * column, top to bottom matching left to right in the document, each with a
 * step-left and step-right button. The selection's column is highlighted so
 * the list ties back to the ring in the preview.
 * @param columns - The table's columns in display order
 * @param onMove - Moves a column one step left or right
 * @returns The column reorder list
 */
export function TableColumnsPanel({ columns, onMove }: TableColumnsPanelProps) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] font-normal">Column order</Label>
      <div className="rounded-md border">
        {columns.map((column, index) => (
          <div
            key={column.nodeId}
            className={cn(
              "flex items-center gap-1 px-2 py-0.5",
              index > 0 && "border-t",
              column.selected && "bg-accent",
            )}
          >
            <span className="min-w-0 flex-1 break-words text-xs">
              {column.label}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="size-6 shrink-0"
              title="Move left"
              aria-label={`Move ${column.label} left`}
              disabled={index === 0}
              onClick={() => onMove(column.nodeId, "left")}
            >
              <ArrowLeft className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-6 shrink-0"
              title="Move right"
              aria-label={`Move ${column.label} right`}
              disabled={index === columns.length - 1}
              onClick={() => onMove(column.nodeId, "right")}
            >
              <ArrowRight className="size-3.5" />
            </Button>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground">
        Top to bottom is left to right in the document.
      </p>
    </div>
  );
}
